// Canonical DeckModel contract for @rdx/deck-system.
//
// The DeckModel is the structured, authoritative source for a deck. It compiles
// to a self-contained deck-system HTML document via `render-deck.ts`. A stored
// Deck keeps the compiled `html` as the render path (the viewer iframe renders
// it unchanged) and gains an OPTIONAL `model` field (this shape) as the editable
// source. Legacy decks have `model` absent/null and still render via `html`.
//
// Compilation happens in the FRONTEND (render-deck.ts) — the server never
// compiles. This file is the single source of truth other code imports; the AI
// generation endpoint (POST /api/decks/generate) returns a DeckModel that
// validates against this shape.
//
// British spelling throughout. No emojis.

/** Theme preference baked into the compiled deck. Maps to `data-theme` on
 *  <html>; "auto" leaves the theme-toggle/localStorage waterfall in charge. */
export type DeckTheme = 'auto' | 'light' | 'dark';

/** Slide variant — maps to the <section> variant class. "content" is the
 *  default and carries no special variant class. */
export type SlideVariant = 'cover' | 'content' | 'divider' | 'closing' | 'statement';

/** Semantic accent token names usable on a card label. Mirrors the Paper
 *  `.label` colour modifiers documented in COMPONENTS.md. */
export type CardAccent = 'ink' | 'red' | 'sage' | 'clay';

/** Deck-wide accent preset names. WAVE 3: re-points --accent/-soft/-on to an
 *  existing semantic token, emitted as `data-accent` on <html>. The renderer
 *  allowlists these — any other value is dropped. */
export type DeckAccent = 'red' | 'ink' | 'sage' | 'clay' | 'info';

/** Deck-level metadata. Drives <title>, the cover slide, and theme. */
export interface DeckMeta {
  title: string;
  subtitle?: string;
  author?: string;
  date?: string;
  theme?: DeckTheme;
  /** Optional deck-wide accent preset. WAVE 3: when this is one of the allowed
   *  presets (red | ink | sage | clay | info) the renderer emits a matching
   *  `data-accent` attribute on <html>, which re-points the --accent token trio
   *  (see W3.1 in deck-system.css). Any other value is dropped (allowlisted in
   *  the renderer), so a hostile value can never break out of the attribute. */
  accent?: DeckAccent;
}

// ── Block discriminated union (on `type`) ────────────────────────────────────

/** An inline cover pill — a monospace key cap followed by dot-separated text
 *  spans. Only honoured on a cover-variant title block; when present it both
 *  emits the `.cover-tag` pill AND promotes the cover slide to `.cover-rich`. */
export interface CoverTag {
  /** Optional monospace key cap, e.g. "⌘K". */
  key?: string;
  /** Dot-separated text spans, e.g. ["Three layers", "Six apps"]. */
  items: string[];
}

/** Title-size override for content/statement title blocks. Unset = the
 *  variant's default size (current behaviour, fully backward-compatible). */
export type TitleSize = 'sm' | 'md' | 'lg' | 'xl';

export interface TitleBlock {
  type: 'title';
  eyebrow?: string;
  title: string;
  lede?: string;
  /** Optional cover-rich pill. On a cover slide this emits the `.cover-tag`
   *  strip and promotes the section to `.cover.cover-rich` (see COMPONENTS.md
   *  "cover.cover-rich"). Ignored on non-cover variants. */
  coverTag?: CoverTag;
  /** Title size override (content / statement variants). Default unset keeps
   *  the variant's current size. */
  size?: TitleSize;
  /** Horizontal alignment of the title block. Default left. */
  align?: 'left' | 'center';
  /** Anchor the title block low-left, for placing a title over a slide
   *  background image. Pair with `slide.background` of kind "image". */
  overlay?: boolean;
}

export interface HeadingBlock {
  type: 'heading';
  level: 1 | 2 | 3;
  text: string;
}

export interface ParagraphBlock {
  type: 'paragraph';
  text: string;
}

export interface LedeBlock {
  type: 'lede';
  text: string;
}

export interface BulletsBlock {
  type: 'bullets';
  items: string[];
  ordered?: boolean;
}

export interface CardItem {
  label?: string;
  title: string;
  desc?: string;
  accent?: CardAccent;
}

export interface CardsBlock {
  type: 'cards';
  columns?: 2 | 3;
  cards: CardItem[];
}

export interface StatItem {
  n: string;
  label: string;
  accent?: 'red';
}

export interface StatRowBlock {
  type: 'stat-row';
  stats: StatItem[];
}

export interface QuoteBlock {
  type: 'quote';
  text: string;
  attribution?: string;
}

export interface TableBlock {
  type: 'table';
  headers: string[];
  rows: string[][];
}

export interface KvItem {
  k: string;
  v: string;
}

export interface KvBlock {
  type: 'kv';
  items: KvItem[];
}

export interface RuleBlock {
  type: 'rule';
}

/** AUDITED escape hatch. The renderer inserts `html` VERBATIM (no escaping).
 *  Only ever populate this from trusted/audited content. */
export interface RawHtmlBlock {
  type: 'raw-html';
  html: string;
}

// ── WAVE 3 blocks ─────────────────────────────────────────────────────────
//
// Image-bearing blocks carry an `image`/`images`/`avatar`/`qr` URL. The
// renderer escapes those for attribute context AND restricts them to safe
// schemes (http/https/root-relative + image/* data URIs) via sanitiseUrl —
// a hostile `javascript:`/non-image `data:` URL becomes an empty src, so no
// script can fire. All text fields are escaped like every other block.

/** A referenced image — `src` is URL-sanitised, `alt` is escaped. */
export interface ImageRef {
  src: string;
  alt?: string;
  /** Only honoured inside a media-grid tile. */
  caption?: string;
}

/** Image one side, content the other. `reverse` swaps the sides. The nested
 *  `blocks` render into the content half exactly like a normal slide body. */
export interface MediaSplitBlock {
  type: 'media-split';
  image: ImageRef;
  reverse?: boolean;
  blocks: Block[];
  /** Optional caption beneath the image. */
  caption?: string;
}

/** Full-bleed background image with a readability scrim and an overlaid
 *  title block (eyebrow + title). */
export interface MediaFullBlock {
  type: 'media-full';
  image: ImageRef;
  title?: string;
  eyebrow?: string;
}

/** A responsive 2–4 tile image grid. Each tile may carry a caption. */
export interface MediaGridBlock {
  type: 'media-grid';
  images: ImageRef[];
}

/** A captioned image card. */
export interface FigureBlock {
  type: 'figure';
  image: ImageRef;
  caption?: string;
}

/** A single hero metric. The value picks up the active accent preset. */
export interface BignumBlock {
  type: 'bignum';
  value: string;
  label?: string;
  /** Optional up/down delta chip. */
  delta?: { dir: 'up' | 'down'; text: string };
  /** Centre-align the metric. */
  center?: boolean;
}

/** A large quote with attribution and an optional avatar/logo image. */
export interface TestimonialBlock {
  type: 'testimonial';
  quote: string;
  name?: string;
  role?: string;
  company?: string;
  /** Avatar/logo image URL (URL-sanitised). */
  avatar?: string;
  /** Render the avatar as a squared logo rather than a round portrait. */
  logo?: boolean;
}

/** A vertical bar chart. Each bar's height is `value / max` (max defaults to
 *  the largest value). Bars walk the chart series ramp by index. */
export interface ChartBarsBlock {
  type: 'chart-bars';
  bars: { label: string; value: number }[];
  max?: number;
}

/** A single-value donut with an optional multi-segment legend. */
export interface DonutBlock {
  type: 'donut';
  /** Percentage 0..100 driving the arc length. */
  value: number;
  label?: string;
  segments?: { label: string; value: number }[];
}

/** A line/area trend. Each series is a list of numeric points. Series walk
 *  the chart ramp by index. */
export interface ChartLineBlock {
  type: 'chart-line';
  series: { label?: string; points: number[] }[];
}

/** A numbered section index (agenda / running TOC). */
export interface AgendaBlock {
  type: 'agenda';
  items: { text: string; active?: boolean; done?: boolean; note?: string }[];
}

/** A comparison table with tick/cross/partial markers. A cell value of
 *  "yes"/"no"/"partial" renders the matching marker; any other string renders
 *  as escaped text. */
export interface CompareBlock {
  type: 'compare';
  headers: string[];
  rows: { label: string; cells: ('yes' | 'no' | 'partial' | string)[] }[];
}

/** A two-column pros / cons block. */
export interface ProsConsBlock {
  type: 'proscons';
  pros: string[];
  cons: string[];
  /** Optional headings (default "Pros" / "Cons"). */
  prosHead?: string;
  consHead?: string;
}

/** An icon + headline + description card. `icon` is an inline SVG string —
 *  treated as an AUDITED raw hatch (sanitised to <svg> markup only) so a
 *  hostile value cannot inject script. See sanitiseIconSvg in render-deck.ts. */
export interface FeatureCardBlock {
  type: 'feature-card';
  icon?: string;
  title: string;
  desc?: string;
}

/** An icon + text list. Each item's `icon` is an inline SVG string subject to
 *  the same audited sanitisation as feature-card. */
export interface IconListBlock {
  type: 'icon-list';
  items: { icon?: string; text: string; sub?: string }[];
}

/** A closing call-to-action slide: accent headline, contact key/value block,
 *  and an optional QR image. */
export interface CtaBlock {
  type: 'cta';
  title: string;
  sub?: string;
  contact?: { k: string; v: string }[];
  /** QR image URL (URL-sanitised). */
  qr?: string;
}

// ── DRIFT-CLOSING blocks ──────────────────────────────────────────────────
//
// These components already ship first-class CSS in src/deck-system.css and are
// documented in components/COMPONENTS.md. The renderer below emits the EXACT
// documented markup/classes so the existing CSS applies — no new CSS. Every
// text field is escaped and every accent/tint/category input is allowlisted.

/** Product-surface tile grid (.apps-hub + .apps-hub-tile). */
export interface AppsHubTile {
  eyebrow?: string;
  name: string;
  url?: string;
  note?: string;
  experimental?: boolean;
}
/** Optional centred Cmd-K switcher header above the apps-hub tile grid
 *  (.apps-hub-switcher). Matches the original "Six apps, one switcher" slide. */
export interface AppsHubSwitcher {
  /** Monospace key cap, e.g. "⌘K". */
  key?: string;
  label: string;
  hint?: string;
}
export interface AppsHubBlock {
  type: 'apps-hub';
  /** Column count (1..6). Defaults to 3 when absent or out of range. */
  cols?: number;
  /** Optional centred switcher rendered above the grid. */
  switcher?: AppsHubSwitcher;
  tiles: AppsHubTile[];
}

/** Capability strip beneath a pricing table (.tier-grid + .tier-cap). */
export interface TierCapItem {
  name: string;
  what: string;
  /** 2px ink-outlined highlight cap (.tier-cap.featured). */
  featured?: boolean;
  /** Editorial Intelligence treatment — Fraunces italic danger (.tier-cap.intel). */
  intel?: boolean;
  /** Render the wrapping grid as a single column (.tier-grid.single). */
  single?: boolean;
}
export interface TierCapBlock {
  type: 'tier-cap';
  caps: TierCapItem[];
}

/** Stakeholder feedback / response pairs (.region-grid + .region-card). */
export interface RegionItem {
  feedback: string;
  response?: string;
}
export interface RegionCardData {
  label: string;
  items: RegionItem[];
}
export interface RegionGridBlock {
  type: 'region-grid';
  regions: RegionCardData[];
}

/** Sources → engine → surfaces architecture diagram (.signal-flow). */
export interface SignalSource {
  /** Dot tint — allowlisted to a fixed set in the renderer. */
  icon?: string;
  name: string;
  sub?: string;
}
export interface SignalEngine {
  name: string;
  sub?: string;
}
export interface SignalSurface {
  name: string;
  eg?: string;
}
export interface SignalFlowBlock {
  type: 'signal-flow';
  sources: SignalSource[];
  engine: SignalEngine;
  surfaces: SignalSurface[];
}

/** Schematic floor plan diagram (.floorplan-card + .fp-*). Data-driven: rooms
 *  are rects, pins are colour-coded callouts, optional KPI footer. */
export interface FloorplanRoom {
  label?: string;
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface FloorplanPin {
  /** App key — allowlisted to emp|man|ins|vis in the renderer. */
  app: string;
  x: number;
  y: number;
  label: string;
  lead?: string;
}
export interface FloorplanKpi {
  n: string;
  label: string;
}
export interface FloorplanBlock {
  type: 'floorplan';
  rooms: FloorplanRoom[];
  pins: FloorplanPin[];
  kpis?: FloorplanKpi[];
}

/** Horizontal architecture bands (.layer-band + .layer-down-arrow). */
export interface LayerBandItem {
  name: string;
  desc: string;
  /** Colour tint — allowlisted to intel|ops|spatial in the renderer. */
  tint?: 'intel' | 'ops' | 'spatial';
}
export interface LayerBandBlock {
  type: 'layer-band';
  bands: LayerBandItem[];
}

/** Layered AI overlay (.ai-stack + .ai-tier + .ai-card). */
export interface AiCard {
  eyebrow?: string;
  title: string;
  desc?: string;
  /** Status dot — allowlisted to live|planned in the renderer. */
  status?: 'live' | 'planned';
  loc?: string;
  featured?: boolean;
}
export interface AiTier {
  name?: string;
  cards: AiCard[];
}
export interface AiStackBlock {
  type: 'ai-stack';
  tiers: AiTier[];
}

/** 3-up shared-engine cards (.engine-grid + .engine-card). */
export interface EngineItem {
  eyebrow?: string;
  title: string;
  code?: string;
  desc?: string;
  quote?: string;
}
export interface EngineGridBlock {
  type: 'engine-grid';
  engines: EngineItem[];
}

/** Delivery-cadence loop (.cycle-wrap + .cycle-points). */
export interface CyclePoint {
  label: string;
  desc?: string;
}
export interface CycleBlock {
  type: 'cycle';
  points: CyclePoint[];
}

/** Horizontal year-axis timeline (.roadmap + .roadmap-svg + .rm-*). */
export interface RoadmapMilestone {
  when: string;
  label: string;
  /** Half-opacity forecast dot (.rm-dot.future). */
  future?: boolean;
}
export interface RoadmapBlock {
  type: 'roadmap';
  milestones: RoadmapMilestone[];
}

/** Horizontal bars (.bar-h + .bar-h-row). */
export interface BarHItem {
  label: string;
  value: number;
  /** Fill tint — allowlisted to warn|ok|muted in the renderer. */
  tone?: 'warn' | 'ok' | 'muted';
}
export interface BarHBlock {
  type: 'bar-h';
  bars: BarHItem[];
  /** Scale maximum; defaults to the largest value. */
  max?: number;
}

/** Per-asset tiered pricing table (.pricing-tbl). Columns carry an optional
 *  editorial Intelligence treatment; rows hold per-cell values and an optional
 *  single-tier strip row spanning the foot. */
export interface PricingColumn {
  label: string;
  /** Editorial Intelligence column header (.tier-name.tier-intel). */
  intel?: boolean;
}
export interface PricingCell {
  value: string;
  /** Intelligence-tier price cell (.price.intel). */
  intel?: boolean;
}
export interface PricingRow {
  cells: (string | PricingCell)[];
  /** A single-tier strip row spanning every column (tr.single-tier). */
  single?: boolean;
}
export interface PricingTblBlock {
  type: 'pricing-tbl';
  columns: PricingColumn[];
  rows: PricingRow[];
}

// ── TIER-C blocks (new CSS by the CSS agent — class names align here) ──────

/** The recurring "the conversation flips" strip (.callout-banner). */
export interface CalloutBannerBlock {
  type: 'callout-banner';
  text: string;
  eyebrow?: string;
  /** Accent — allowlisted to red|sage|clay|ink|info; defaults to red. */
  accent?: 'red' | 'sage' | 'clay' | 'ink' | 'info';
}

/** The "Spatial × Workplace × Intelligence = Workplace OS" header (.equation). */
export interface EquationBlock {
  type: 'equation';
  terms: string[];
  result: string;
}

/** A labelled wall of category-coloured chips (.chip-wall). */
export interface ChipWallItem {
  label: string;
  /** Category — allowlisted to a small token palette in the renderer. */
  category?: string;
  sub?: string;
}
export interface ChipWallLegendItem {
  label: string;
  category: string;
}
export interface ChipWallBlock {
  type: 'chip-wall';
  title?: string;
  chips: ChipWallItem[];
  legend?: ChipWallLegendItem[];
}

/** The Cmd-K centrepiece hub (.command-hub). */
export interface CommandHubBlock {
  type: 'command-hub';
  label: string;
  hint?: string;
  items?: string[];
}

/** The before/after fragmentation pair (.before-after). */
export interface BeforeAfterColumn {
  title: string;
  items: string[];
}
export interface BeforeAfterBlock {
  type: 'before-after';
  before: BeforeAfterColumn;
  after: BeforeAfterColumn;
}

// ── V5 blocks (flexible layouts + restored composites) ────────────────────
//
// Shared detail payload for click-to-expand cards/markers. It feeds the deck's
// detail modal via DeckOverlays.registerDetails — the renderer emits the data
// RAW (JSON-encoded), and src/deck-overlays.js injects it with textContent (NOT
// innerHTML), so hostile strings can never break out. Do NOT pre-escape these.
export interface DetailRef {
  label?: string;
  title?: string;
  /** One paragraph per string. */
  body?: string[];
  /** Footer key/value pairs. */
  meta?: { k: string; v: string }[];
}

/** A generic two-column split — the one "smart layout" primitive. Each side is
 *  a FLAT list of blocks (keep it shallow; auto-fit shrinks an overfull slide).
 *  Composes side-by-side text/image, the VoC composite, reasons two-card, etc. */
export interface SplitBlock {
  type: 'split';
  /** Column ratio — allowlisted in the renderer; defaults to '1-1'. */
  ratio?: '1-1' | '5-7' | '7-5' | '4-6' | '6-4' | '2-1' | '1-2';
  /** Cross-axis alignment of the columns; defaults to 'top'. */
  align?: 'top' | 'center';
  left: Block[];
  right: Block[];
}

/** A titled card that wraps a flat list of blocks (Gamma's "card holds
 *  content"). Optional detail makes the whole card click-to-expand. */
export interface PanelBlock {
  type: 'panel';
  label?: string;
  title?: string;
  /** Navy accent panel (.panel.accent). */
  accent?: boolean;
  /** Click-to-expand into the detail modal. */
  detail?: DetailRef;
  blocks: Block[];
}

/** A year-axis timeline with markers above/below the axis + an optional
 *  secondary "ongoing" rail (.timeline). Distinct from `roadmap` (kept as-is). */
export interface TimelineMarker {
  /** Calendar label under the dot (e.g. "2020" or "Apr 2026"). */
  when?: string;
  label: string;
  sub?: string;
  /** 0..1 position across the axis. Omit to spread markers evenly. */
  at?: number;
  /** Place the callout above or below the axis; omit to alternate. */
  side?: 'above' | 'below';
  /** Emphasised future/now marker (ringed dot + danger sub). */
  future?: boolean;
  /** Optional click-to-expand detail (renders a clickable .tl-dot). */
  detail?: DetailRef;
}
export interface TimelineRailItem {
  /** 0..1 position. */
  at: number;
  label: string;
}
export interface TimelineBlock {
  type: 'timeline';
  /** Axis tick labels left→right (e.g. ["2020",…,"2026"]). */
  ticks?: string[];
  markers: TimelineMarker[];
  /** Optional secondary rail under the axis (e.g. "ONGOING"). */
  ongoing?: { label?: string; items?: TimelineRailItem[] };
}

/** A feature/advantage card grid — icon + eyebrow + title + desc, optional
 *  per-card detail → modal, one optional navy accent card (.feature-grid). */
export interface FeatureGridItem {
  /** Trusted inline <svg> icon string (sanitised like other icon fields). */
  icon?: string;
  eyebrow?: string;
  title: string;
  desc?: string;
  /** Navy accent card (.feature-card.accent). */
  accent?: boolean;
  /** Click-to-expand into the detail modal (renders a clickable card with +). */
  detail?: DetailRef;
}
export interface FeatureGridBlock {
  type: 'feature-grid';
  /** Column count 2..4; defaults to 3. */
  columns?: number;
  items: FeatureGridItem[];
}

/** Labelled value/total progress bars (the deck's signature bar — .progress).
 *  Distinct from bar-h (value/max only): each bar shows a label + a value
 *  caption over a track/fill. */
export interface ProgressItem {
  label: string;
  /** Filled amount. */
  value: number;
  /** Scale; bar fills value/total. If absent, value is a 0-100 percentage. */
  total?: number;
  /** Right-hand caption override (e.g. "9 / 11"); defaults to value/total. */
  display?: string;
  /** Coral emphasis (danger fill + danger label). */
  accent?: boolean;
}
export interface ProgressBlock {
  type: 'progress';
  items: ProgressItem[];
}

export type Block =
  | TitleBlock
  | HeadingBlock
  | ParagraphBlock
  | LedeBlock
  | BulletsBlock
  | CardsBlock
  | StatRowBlock
  | QuoteBlock
  | TableBlock
  | KvBlock
  | RuleBlock
  | RawHtmlBlock
  | MediaSplitBlock
  | MediaFullBlock
  | MediaGridBlock
  | FigureBlock
  | BignumBlock
  | TestimonialBlock
  | ChartBarsBlock
  | DonutBlock
  | ChartLineBlock
  | AgendaBlock
  | CompareBlock
  | ProsConsBlock
  | FeatureCardBlock
  | IconListBlock
  | CtaBlock
  // ── Drift-closing ──
  | AppsHubBlock
  | TierCapBlock
  | RegionGridBlock
  | SignalFlowBlock
  | FloorplanBlock
  | LayerBandBlock
  | AiStackBlock
  | EngineGridBlock
  | CycleBlock
  | RoadmapBlock
  | BarHBlock
  | PricingTblBlock
  // ── TIER-C ──
  | CalloutBannerBlock
  | EquationBlock
  | ChipWallBlock
  | CommandHubBlock
  | BeforeAfterBlock
  // ── V5 ──
  | SplitBlock
  | PanelBlock
  | TimelineBlock
  | FeatureGridBlock
  | ProgressBlock;

/** The discriminant string-literal of `Block.type`. */
export type BlockType = Block['type'];

/** Canonical, ordered list of every allowed block type. Other code (validators,
 *  the AI system prompt, tests) imports this rather than re-declaring it. */
export const BLOCK_TYPES = [
  'title',
  'heading',
  'paragraph',
  'lede',
  'bullets',
  'cards',
  'stat-row',
  'quote',
  'table',
  'kv',
  'rule',
  'raw-html',
  // ── Wave 3 ──
  'media-split',
  'media-full',
  'media-grid',
  'figure',
  'bignum',
  'testimonial',
  'chart-bars',
  'donut',
  'chart-line',
  'agenda',
  'compare',
  'proscons',
  'feature-card',
  'icon-list',
  'cta',
  // ── Drift-closing (CSS already in deck-system.css) ──
  'apps-hub',
  'tier-cap',
  'region-grid',
  'signal-flow',
  'floorplan',
  'layer-band',
  'ai-stack',
  'engine-grid',
  'cycle',
  'roadmap',
  'bar-h',
  'pricing-tbl',
  // ── TIER-C (new CSS) ──
  'callout-banner',
  'equation',
  'chip-wall',
  'command-hub',
  'before-after',
  // ── V5 ──
  'split',
  'panel',
  'timeline',
  'feature-grid',
  'progress',
] as const satisfies readonly BlockType[];

/** Canonical list of every allowed slide variant. */
export const SLIDE_VARIANTS = [
  'cover',
  'content',
  'divider',
  'closing',
  'statement',
] as const satisfies readonly SlideVariant[];

/** Canonical, ordered list of every allowed deck accent preset. The renderer
 *  allowlists `meta.accent` against this set before emitting `data-accent`. */
export const DECK_ACCENTS = [
  'red',
  'ink',
  'sage',
  'clay',
  'info',
] as const satisfies readonly DeckAccent[];

// ── Slide-level background ────────────────────────────────────────────────
//
// A full-bleed layer rendered BEHIND the slide content. The renderer emits it
// as an absolutely-positioned first child of the <section>; deck-stage.js then
// hoists any absolute/fixed child out of the auto-fit wrapper, so the background
// fills the section edge-to-edge and is NEVER scaled by auto-fit (the content
// still scales to fit). All enum-ish fields below are ALLOW-LISTED by the
// renderer against the canonical arrays — an unknown value degrades to a safe
// default, never an injected raw value. Colours map to Paper `var(--…)` tokens.

/** The four background fill kinds. `motion` is animated CSS (no asset);
 *  real `<video>` backgrounds are a documented fast-follow. */
export type BackgroundKind = 'color' | 'gradient' | 'image' | 'motion';

/** Allow-listed colour token names. The renderer maps each to a Paper
 *  `var(--…)`; raw colour values are never accepted. */
export type BackgroundToken =
  | 'paper'
  | 'surface'
  | 'sunken'
  | 'ink'
  | 'navy'
  | 'plum'
  | 'sage'
  | 'clay';

/** Readability veil drawn over the background, under the content. */
export type BackgroundScrim = 'none' | 'soft' | 'strong' | 'top' | 'bottom';

/** Forces the content text colour for legibility over the background. */
export type BackgroundTone = 'light' | 'dark';

/** Animated CSS background presets (kind "motion"). */
export type MotionPreset = 'drift' | 'aurora' | 'mesh';

export interface SlideBackground {
  kind: BackgroundKind;
  /** kind "color": the fill token. */
  token?: BackgroundToken;
  /** kind "gradient": the two stop tokens + optional angle in degrees. */
  from?: BackgroundToken;
  to?: BackgroundToken;
  angle?: number;
  /** kind "image": src (local path, http(s), or "generate:<subject>") +
   *  optional focal position. */
  src?: string;
  position?: 'center' | 'top' | 'bottom';
  /** kind "motion": the animated preset. */
  preset?: MotionPreset;
  /** Shared: legibility veil over the background. */
  scrim?: BackgroundScrim;
  /** Shared: flip content text colour for legibility over the background. */
  tone?: BackgroundTone;
}

/** Canonical, ordered allow-lists — the renderer, schema and tests derive the
 *  valid set from these so the vocabulary is defined once. */
export const BACKGROUND_KINDS = [
  'color',
  'gradient',
  'image',
  'motion',
] as const satisfies readonly BackgroundKind[];

export const BACKGROUND_TOKENS = [
  'paper',
  'surface',
  'sunken',
  'ink',
  'navy',
  'plum',
  'sage',
  'clay',
] as const satisfies readonly BackgroundToken[];

export const BACKGROUND_SCRIMS = [
  'none',
  'soft',
  'strong',
  'top',
  'bottom',
] as const satisfies readonly BackgroundScrim[];

export const BACKGROUND_TONES = ['light', 'dark'] as const satisfies readonly BackgroundTone[];

export const MOTION_PRESETS = [
  'drift',
  'aurora',
  'mesh',
] as const satisfies readonly MotionPreset[];

export const TITLE_SIZES = ['sm', 'md', 'lg', 'xl'] as const satisfies readonly TitleSize[];

export interface Slide {
  /** Maps to the <section> variant class; default "content". */
  variant?: SlideVariant;
  /** data-label / screen label (e.g. "01 Cover"). */
  label?: string;
  /** Optional extra <section> classes (e.g. "s-ink"). */
  class?: string;
  /** Optional full-bleed background layer behind the content. */
  background?: SlideBackground;
  /** Ordered content blocks. */
  blocks: Block[];
  /** Suppress slide chrome (the footer-meta). The title is already optional
   *  (omit the title block); set this for bare image-first / statement slides. */
  bare?: boolean;
  /** Per-slide speaker note (alternative to the parallel `speakerNotes` array). */
  speakerNote?: string;
}

export interface DeckModel {
  meta: DeckMeta;
  slides: Slide[];
  /** Optional parallel array — one note per slide. Takes precedence over
   *  per-slide `speakerNote` when both are present for a given index. */
  speakerNotes?: string[];
}
